// R115 calendar hub — HK "Plan cleans": the smart scheduler's proposal for the
// next 7 days in the form sheet (GET /api/assetops/housekeeping/schedule/propose,
// read-only), the office ticks what to keep, then POST /housekeeping/schedule/apply
// (strict ao_turns chain; it skips turns assigned since the proposal and holds
// back over-capacity rows unless "Apply anyway" → override:true).
import type { CalendarCtx } from '../registry';
import { planRows, applyBody, type PlanRow } from '../../../../../lib/calendarOps.mjs';
import { getJson, postJson, busy, sheetHtml, trackDirty, say } from './util';

const PROPOSE = '/api/assetops/housekeeping/schedule/propose?days=7';
const APPLY = '/api/assetops/housekeeping/schedule/apply';

export function openPlanCleans(ctx: CalendarCtx): void {
  const root = ctx.openSheet(sheetHtml(ctx, '<span>The scheduler’s picks for the next 7 days — untick any you don’t want. Nothing changes until you apply.</span>',
    `<div class="opv-loading">Asking the scheduler…</div>`,
    `<button type="button" class="btn-secondary" data-opv-cancel>Cancel</button><button type="button" class="btn-primary" data-opv-apply disabled>Apply</button>`),
  { title: 'Plan cleans', onClose: () => ctx.markDirty(false) });
  trackDirty(ctx, root);
  root.querySelector('[data-opv-cancel]')?.addEventListener('click', () => { ctx.markDirty(false); ctx.closeSheet(); });
  void load(ctx, root);
}

async function load(ctx: CalendarCtx, root: HTMLElement): Promise<void> {
  const body = root.querySelector('.opv-sheet-body') as HTMLElement;
  const r = await getJson(PROPOSE);
  if (!r.ok || !r.json) { body.innerHTML = `<div class="opv-note opv-bad">${ctx.esc(r.error || 'The scheduler is unavailable')} — nothing was changed.</div>`; return; }
  const rows = planRows(r.json);
  const picked = new Set(rows.filter(x => x.cleaner_id).map(x => x.turn_id));
  if (!rows.length) { body.innerHTML = `<div class="opv-note opv-ok">Every clean in the next 7 days already has a cleaner.</div>`; return; }
  const byDay = new Map<string, PlanRow[]>();
  for (const x of rows) { if (!byDay.has(x.date)) byDay.set(x.date, []); byDay.get(x.date)!.push(x); }
  body.innerHTML = [...byDay.entries()].map(([d, list]) => `<div class="opv-group"><div class="opv-group-h">${ctx.esc(ctx.fmt.wmd(d))} · ${list.length} to place</div>`
    + list.map(x => `<label class="opv-row opv-plan${x.cleaner_id ? '' : ' opv-row-off'}">`
      + `<input type="checkbox" data-opv-turn="${ctx.esc(x.turn_id)}"${x.cleaner_id ? ' checked' : ' disabled'} aria-label="Apply ${ctx.esc(x.listing)}">`
      + `<span class="opv-row-main"><span class="opv-row-line"><b>${ctx.esc(x.listing)}</b>${x.same_day ? '<span class="opv-pill opv-warn">same-day</span>' : ''}</span><small>${x.cleaner_id ? `→ ${ctx.esc(x.cleaner || '')}` : ctx.esc(x.reason || 'no cleaner fits')}${x.est_minutes ? ` · ~${Math.round(x.est_minutes)} min` : ''}</small></span></label>`).join('')
    + `</div>`).join('') + (r.json.vendor_turns_skipped ? `<p class="opv-muted">${Number(r.json.vendor_turns_skipped)} vendor-cleaned turn(s) are left to their vendor.</p>` : '');
  const apply = root.querySelector('[data-opv-apply]') as HTMLButtonElement;
  const label = () => { apply.textContent = `Apply ${picked.size}`; apply.disabled = picked.size === 0; };
  label();
  body.addEventListener('change', (e) => {
    const cb = (e.target as HTMLElement).closest('[data-opv-turn]') as HTMLInputElement | null;
    if (!cb) return;
    if (cb.checked) picked.add(cb.dataset.opvTurn || ''); else picked.delete(cb.dataset.opvTurn || '');
    label();
  });
  apply.addEventListener('click', async () => {
    apply.disabled = true;
    await send(ctx, root, rows, picked, false);
    label();
  });
}

async function send(ctx: CalendarCtx, root: HTMLElement, rows: PlanRow[], picked: Set<string>, override: boolean): Promise<void> {
  say(root, 'Applying…');
  const r = await busy(() => postJson(APPLY, { ...applyBody(rows, picked, override), source: 'calendar' }));
  if (!r.ok || !r.json) { say(root, ctx.esc(r.error), 'bad'); return; }
  const j = r.json;
  const over = Array.isArray(j.over_cap) ? j.over_cap : [];
  if (over.length && !override) {
    const ids = new Set<string>(over.map((o: any) => String(o.turn_id)));
    const m = say(root, `${Number(j.applied) || 0} assigned. ${over.length} held back — over capacity: `
      + over.slice(0, 4).map((o: any) => ctx.esc(`${o.listing_name} → ${o.cleaner} (${(o.warnings || []).map((w: any) => w.message).join('; ')})`)).join(' · ')
      + ` <button type="button" class="btn-secondary btn-sm" data-opv-anyway>Assign anyway</button> <button type="button" class="btn-ghost btn-sm" data-opv-done>Done</button>`, 'warn');
    m?.querySelector('[data-opv-anyway]')?.addEventListener('click', () => { void send(ctx, root, rows.filter(x => ids.has(x.turn_id)), ids, true); }, { once: true });
    m?.querySelector('[data-opv-done]')?.addEventListener('click', () => { void finish(ctx, Number(j.applied) || 0); }, { once: true });
    return;
  }
  await finish(ctx, Number(j.applied) || 0, Number(j.skipped) || 0);
}
async function finish(ctx: CalendarCtx, applied: number, skipped = 0): Promise<void> {
  ctx.markDirty(false);
  ctx.closeSheet();
  ctx.toast(`${applied} clean${applied === 1 ? '' : 's'} assigned${skipped ? ` · ${skipped} skipped (assigned since)` : ''}`);
  await ctx.reload({ base: true });
}
